import useCreateAccountTerm from '@/api/mutations/account/useCreateAccountTerm';
import useUpdateAccountTerm from '@/api/mutations/account/useUpdateAccountTerm';
import meKeys from '@/api/queries/me/keyFactory';
import useGetMyAccountTerms from '@/api/queries/me/useGetMyAccountTerms';
import {
  Button,
  Field,
  Modal,
  ModalProps,
  RichTextarea,
  RichTextContent,
  SkeletonLoader,
  useToastContext,
} from '@payaca/ambrosia';

import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

type TFormState = {
  content: RichTextContent;
};

const formSchema = z.object({
  content: z.any(),
});

const CreateEditWrittenTermContent: FC<{
  onSubmit: (state: TFormState) => void;
  defaultValues?: TFormState;
  isProcessing?: boolean;
}> = (props) => {
  const { onSubmit, isProcessing, defaultValues } = props;

  const formMethods = useForm<TFormState>({
    defaultValues,
  });

  return (
    <form onSubmit={formMethods.handleSubmit(onSubmit, console.error)}>
      <Modal.Body>
        <Field.Legacy>
          <Controller
            render={({ field: { onChange, value } }) => {
              return <RichTextarea value={value} onChange={onChange} />;
            }}
            name="content"
            control={formMethods.control}
          />
        </Field.Legacy>
      </Modal.Body>

      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button isProcessing={isProcessing} type="submit">
            {defaultValues ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </form>
  );
};

export interface IProps extends Omit<ModalProps, 'title'> {
  termId?: string;
}

const CreateEditWrittenTerm: FC<IProps> = (props) => {
  const { termId, onClose, ...rest } = props;

  const { pushError } = useToastContext();

  /**
   * Queries
   */
  const queryClient = useQueryClient();
  const { terms, isPending: isFetchingAccountTerms } = useGetMyAccountTerms({
    types: ['WRITTEN'],
  });

  /**
   * Mutations
   */
  const { mutateAsync: createAccountTerm, isPending: isCreatingTerm } =
    useCreateAccountTerm();
  const { mutateAsync: updateAccountTerm, isPending: isUpdatingTerm } =
    useUpdateAccountTerm();

  const isEditTerm = !!termId && termId !== 'new';

  const handleSubmit = async (state: TFormState) => {
    if (isEditTerm) {
      await updateAccountTerm({
        writtenTerm: {
          termId,
          content: state.content,
        },
      }).catch(() => {
        pushError('Failed to update term');
      });
    } else {
      await createAccountTerm({
        writtenTerm: {
          content: state.content,
        },
      }).catch(() => {
        pushError('Failed to create term');
      });
    }

    await queryClient.invalidateQueries({
      queryKey: meKeys.accountTerms(),
    });

    onClose?.();
  };

  const foundTerm = terms?.find((term) => {
    return term.__typename === 'WrittenTerm' && term.id === termId;
  });

  return (
    <Modal
      title={isEditTerm ? 'Update required term' : 'Create required term'}
      onClose={onClose}
      {...rest}
    >
      {isFetchingAccountTerms ? (
        <Modal.Body>
          <SkeletonLoader.Textarea />
        </Modal.Body>
      ) : (
        <CreateEditWrittenTermContent
          defaultValues={
            isEditTerm && foundTerm && 'content' in foundTerm
              ? {
                  content: foundTerm.content,
                }
              : undefined
          }
          isProcessing={isCreatingTerm || isUpdatingTerm}
          onSubmit={handleSubmit}
        />
      )}
    </Modal>
  );
};

export default CreateEditWrittenTerm;
